import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar as CalendarIcon, Clock, MapPin, ChevronLeft, ChevronRight, Loader2, ExternalLink, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { eventosAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Función helper para parsear fechas en cualquier formato
const parseEventDate = (dateValue) => {
  if (!dateValue) return null;
  
  // Si ya es un objeto Date válido
  if (dateValue instanceof Date && !isNaN(dateValue)) {
    return dateValue;
  }
  
  // Convertir a string si no lo es
  const dateStr = String(dateValue);
  
  // Intentar parseo directo (ISO format)
  let date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  // Formato: "YYYY-MM-DD HH:MM:SS" (MySQL/MariaDB)
  const mysqlMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (mysqlMatch) {
    const [, year, month, day, hour, min, sec] = mysqlMatch;
    return new Date(year, month - 1, day, hour, min, sec);
  }
  
  // Formato: "DD/MM/YYYY HH:MM" (Español)
  const esMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
  if (esMatch) {
    const [, day, month, year, hour, min] = esMatch;
    return new Date(year, month - 1, day, hour, min);
  }
  
  // Formato: "DD/MM/YYYY" (Solo fecha)
  const esDateOnly = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (esDateOnly) {
    const [, day, month, year] = esDateOnly;
    return new Date(year, month - 1, day);
  }
  
  // Formato timestamp Unix (segundos o milisegundos)
  if (/^\d+$/.test(dateStr)) {
    const num = parseInt(dateStr);
    // Si es menor que año 3000 en milisegundos, asumir segundos
    return new Date(num < 100000000000 ? num * 1000 : num);
  }
  
  console.warn('[Agenda] No se pudo parsear fecha:', dateValue);
  return null;
};

const Agenda = () => {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const loadData = useCallback(async () => {
    console.log('[Agenda] Iniciando carga de datos...');
    try {
      setLoading(true);
      setError(null);
      
      // Cargar eventos
      console.log('[Agenda] Llamando a eventosAPI.getAll()...');
      const eventosRes = await eventosAPI.getAll();
      console.log('[Agenda] Respuesta eventos raw:', eventosRes);
      
      // Manejar diferentes formatos de respuesta
      let eventosData = [];
      if (eventosRes?.data) {
        if (Array.isArray(eventosRes.data)) {
          eventosData = eventosRes.data;
        } else if (eventosRes.data.data && Array.isArray(eventosRes.data.data)) {
          eventosData = eventosRes.data.data;
        }
      }
      console.log('[Agenda] Eventos procesados:', eventosData.length);
      setEventos(eventosData);
      
      // Cargar expedientes
      console.log('[Agenda] Llamando a expedientesAPI.getAll()...');
      const expedientesRes = await expedientesAPI.getAll();
      console.log('[Agenda] Respuesta expedientes raw:', expedientesRes);
      
      let expData = [];
      if (expedientesRes?.data) {
        if (Array.isArray(expedientesRes.data)) {
          expData = expedientesRes.data;
        } else if (expedientesRes.data.data && Array.isArray(expedientesRes.data.data)) {
          expData = expedientesRes.data.data;
        }
      }
      console.log('[Agenda] Expedientes procesados:', expData.length);
      setExpedientes(expData);
      
      console.log('[Agenda] Carga completada exitosamente');
      
    } catch (err) {
      console.error('[Agenda] Error cargando datos:', err);
      console.error('[Agenda] Error response:', err.response);
      setError(err.message || 'Error al cargar los datos');
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los eventos. Revisa la consola para más detalles.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateEvento = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const expedienteId = formData.get('expediente');
    
    const fechaStr = formData.get('fecha');
    const horaStr = formData.get('hora') || '09:00';
    const fechaInicio = new Date(`${fechaStr}T${horaStr}:00`);
    const fechaFin = new Date(fechaInicio.getTime() + 60 * 60 * 1000);
    
    const newEvento = {
      titulo: formData.get('titulo'),
      expediente_id: expedienteId ? parseInt(expedienteId) : null,
      fecha_inicio: fechaInicio.toISOString(),
      fecha_fin: fechaFin.toISOString(),
      calendario: formData.get('tipo')?.toLowerCase() || 'reunion',
      tipo: formData.get('tipo'),
      estado: 'pendiente',
      todo_el_dia: false,
      descripcion: formData.get('notas') || '',
      asignados: formData.get('responsable') || ''
    };
    
    try {
      await eventosAPI.create(newEvento);
      await loadData();
      setIsCreateDialogOpen(false);
      toast({
        title: '¡Evento creado!',
        description: `${newEvento.titulo} añadido al calendario`,
      });
    } catch (error) {
      console.error('Error creando evento:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear el evento',
        variant: 'destructive'
      });
    }
  };

  const getEventosByDate = (date) => {
    if (!Array.isArray(eventos)) return [];
    return eventos.filter(evento => {
      if (!evento?.fecha_inicio) return false;
      const eventoDate = parseEventDate(evento.fecha_inicio);
      if (!eventoDate) return false;
      return eventoDate.toDateString() === date.toDateString();
    });
  };

  const getTipoBadge = (tipo) => {
    const colors = {
      'audiencia': 'bg-red-100 text-red-800',
      'audiencia_previa': 'bg-red-100 text-red-800',
      'reunion': 'bg-blue-100 text-blue-800',
      'plazo': 'bg-orange-100 text-orange-800',
      'conciliacion': 'bg-purple-100 text-purple-800',
      'juicio': 'bg-red-200 text-red-900',
      'vista': 'bg-pink-100 text-pink-800'
    };
    const tipoLower = (tipo || '').toLowerCase();
    return colors[tipoLower] || 'bg-gray-100 text-gray-800';
  };

  const formatTipo = (tipo) => {
    if (!tipo) return 'Evento';
    return tipo.charAt(0).toUpperCase() + tipo.slice(1).replace(/_/g, ' ');
  };

  const changeMonth = (increment) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setSelectedDate(newDate);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const daysInMonth = getDaysInMonth(selectedDate);
  const eventosHoy = getEventosByDate(selectedDate);
  const todosEventos = Array.isArray(eventos) ? [...eventos].sort((a, b) => {
    const dateA = parseEventDate(a.fecha_inicio);
    const dateB = parseEventDate(b.fecha_inicio);
    if (!dateA || !dateB) return 0;
    return dateA - dateB;
  }) : [];
  const eventosProximos = todosEventos.filter(e => {
    const fecha = parseEventDate(e.fecha_inicio);
    return fecha && fecha >= new Date();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="text-red-600 font-medium">{error}</p>
        <Button onClick={loadData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agenda</h1>
          <p className="text-gray-600 mt-1">Gestiona eventos, citas y plazos ({eventos.length} eventos)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Evento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Evento</DialogTitle>
                <DialogDescription>
                  Añade un evento al calendario
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateEvento}>
                <div className="grid gap-4 py-4">
                  <div>
                    <Label htmlFor="titulo">Título del evento</Label>
                    <Input id="titulo" name="titulo" required />
                  </div>
                  <div>
                    <Label htmlFor="tipo">Tipo de evento</Label>
                    <Select name="tipo" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="audiencia">Audiencia</SelectItem>
                        <SelectItem value="audiencia_previa">Audiencia Previa</SelectItem>
                        <SelectItem value="reunion">Reunión</SelectItem>
                        <SelectItem value="plazo">Plazo</SelectItem>
                        <SelectItem value="conciliacion">Conciliación</SelectItem>
                        <SelectItem value="juicio">Juicio</SelectItem>
                        <SelectItem value="vista">Vista</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="expediente">Expediente asociado</Label>
                    <Select name="expediente">
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar expediente (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.isArray(expedientes) && expedientes.map(exp => (
                          <SelectItem key={exp.id} value={String(exp.id)}>
                            {exp.numero_expediente} - {exp.titulo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fecha">Fecha</Label>
                      <Input id="fecha" name="fecha" type="date" required />
                    </div>
                    <div>
                      <Label htmlFor="hora">Hora</Label>
                      <Input id="hora" name="hora" type="time" defaultValue="09:00" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="responsable">Responsable</Label>
                    <Input id="responsable" name="responsable" placeholder="Nombre del responsable" />
                  </div>
                  <div>
                    <Label htmlFor="notas">Notas</Label>
                    <Textarea id="notas" name="notas" placeholder="Observaciones o detalles adicionales" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Crear Evento
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button variant="ghost" size="icon" onClick={() => changeMonth(-1)}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-lg font-semibold">
                {selectedDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => changeMonth(1)}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Days of Week */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {daysInMonth.map((day, index) => {
                const dayEventos = day ? getEventosByDate(day) : [];
                const isToday = day && day.toDateString() === new Date().toDateString();
                const isSelected = day && day.toDateString() === selectedDate.toDateString();
                
                return (
                  <div
                    key={index}
                    className={`min-h-[80px] p-1 border rounded-lg cursor-pointer transition-colors
                      ${!day ? 'bg-gray-50' : 'hover:bg-gray-50'}
                      ${isToday ? 'border-blue-500 bg-blue-50' : ''}
                      ${isSelected ? 'ring-2 ring-blue-500' : ''}
                    `}
                    onClick={() => day && setSelectedDate(day)}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>
                          {day.getDate()}
                        </div>
                        <div className="space-y-1">
                          {dayEventos.slice(0, 2).map(evento => (
                            <div
                              key={evento.id}
                              className={`text-xs p-1 rounded truncate ${getTipoBadge(evento.calendario || evento.tipo)}`}
                              title={evento.titulo}
                            >
                              {evento.titulo}
                            </div>
                          ))}
                          {dayEventos.length > 2 && (
                            <div className="text-xs text-gray-500">
                              +{dayEventos.length - 2} más
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sidebar - Próximos Eventos */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-4">Próximos Eventos</h3>
              {eventosProximos.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay eventos próximos</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {eventosProximos.slice(0, 10).map(evento => {
                    const fecha = parseEventDate(evento.fecha_inicio);
                    const expediente = expedientes.find(e => e.id === evento.expediente_id);
                    if (!fecha) return null;
                    return (
                      <div 
                        key={evento.id} 
                        className={`flex gap-3 p-2 rounded-lg hover:bg-gray-50 ${evento.expediente_id ? 'cursor-pointer' : ''}`}
                        onClick={() => evento.expediente_id && navigate(`/expedientes/${evento.expediente_id}`)}
                      >
                        <div className="flex-shrink-0 w-12 text-center">
                          <div className="text-2xl font-bold text-gray-800">
                            {fecha.getDate()}
                          </div>
                          <div className="text-xs text-gray-500 uppercase">
                            {fecha.toLocaleDateString('es-ES', { month: 'short' })}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{evento.titulo}</div>
                          {expediente && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/expedientes/${evento.expediente_id}`);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate flex items-center gap-1"
                            >
                              {expediente.numero_expediente}
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-xs ${getTipoBadge(evento.calendario || evento.tipo)}`}>
                              {formatTipo(evento.calendario || evento.tipo)}
                            </Badge>
                            {evento.fecha_inicio && (
                              <span className="text-xs text-gray-500 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Eventos del día seleccionado */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-4">
                Eventos del {selectedDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
              </h3>
              {eventosHoy.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay eventos para este día</p>
              ) : (
                <div className="space-y-2">
                  {eventosHoy.map(evento => {
                    const fecha = parseEventDate(evento.fecha_inicio);
                    const expediente = expedientes.find(e => e.id === evento.expediente_id);
                    if (!fecha) return null;
                    return (
                      <div 
                        key={evento.id} 
                        className={`p-2 border rounded-lg ${evento.expediente_id ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                        onClick={() => evento.expediente_id && navigate(`/expedientes/${evento.expediente_id}`)}
                      >
                        <div className="font-medium text-sm">{evento.titulo}</div>
                        {expediente && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/expedientes/${evento.expediente_id}`);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 mt-1"
                          >
                            {expediente.numero_expediente}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          <Badge className={`ml-auto ${getTipoBadge(evento.calendario || evento.tipo)}`}>
                            {formatTipo(evento.calendario || evento.tipo)}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Agenda;
